import {
  APIReference,
  APIReference_apiReference as AutoGenAPIReferenceType,
  ClassReferenceParent,
  ReferenceType,
} from '@modules/client';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppLayout, { LayoutType, TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppLeftNav from '@modules/layout/layout/components/AppLeftNav';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import SideBarNavigation from '@modules/ui/shared/components/SideBarNavigation';
import { rightNavMinBreakpoint } from '@modules/layout/layout/constants/layoutConstants';
import { useTranslation } from '@rbx/intl';
import { Typography, useMediaQuery } from '@rbx/ui';
import React, { useMemo, useState, useEffect } from 'react';
import { TestId } from '@modules/utilities/constants/testConstants';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import { EngineTag } from '@modules/ui/tags/types';
import ShowInViewMetadataProvider from '@modules/ui/shared/components/ShowInViewMetadataProvider';
import {
  getAPIReferenceHeadings,
  isClassReference,
  isDatatypeReference,
  isEnumReference,
  isGlobalReference,
  isLibraryReference,
} from '../../utils/apiReferenceUtils';
import { headingTranslKeyByReferenceType } from '../../constants/apiReferenceConstants';
import ReferenceHeading from './ReferenceHeading';
import EngineReferenceRightNavContent from './EngineReferenceRightNavContent';
import ClassContent from '../../class/components/ClassContent';
import DatatypeContent from '../../datatype/components/DatatypeContent';
import EnumContent from '../../enum/components/EnumContent';
import GlobalOrLibraryContent from '../../globalOrLibrary/components/GlobalOrLibraryContent';
import { filterEngineReferenceHeadings } from '../utils/engineReferenceRightNavUtils';

interface ReferenceContainerV2Props {
  title: string;
  apiReference: AutoGenAPIReferenceType | APIReference['apiReference'];
  classReferenceParents: ClassReferenceParent[] | null;
  openGraphMeta: Partial<TOpenGraphMeta>;
  description: string;
}

export default function ReferenceContainerV2({
  title,
  apiReference,
  classReferenceParents,
  openGraphMeta,
  description,
}: ReferenceContainerV2Props) {
  const { translate } = useTranslation();
  const isAppRightNavCompatible = useMediaQuery((theme) =>
    theme.breakpoints.up(rightNavMinBreakpoint),
  );
  const [showRightNav, setShowRightNav] = useState(false);
  const toggleRightNav = () => setShowRightNav((prev) => !prev);

  useEffect(() => {
    // Set showRightNav to true if the screen is large enough to support the app right nav
    // Automatically closes the right nav if the screen is too small to support it
    setShowRightNav(isAppRightNavCompatible);
  }, [isAppRightNavCompatible]);

  const headings = useMemo(() => {
    if (apiReference === null) {
      return [];
    }
    const rawHeadings = getAPIReferenceHeadings(
      translate,
      apiReference,
      classReferenceParents,
      true,
    );
    return filterEngineReferenceHeadings(rawHeadings);
  }, [translate, apiReference, classReferenceParents]);

  const hasRightNavItems = headings.length > 0;

  const content = useMemo(() => {
    if (apiReference === null) return null;
    if (isClassReference(apiReference)) {
      return (
        <ClassContent
          classReference={apiReference}
          classReferenceParents={classReferenceParents}
          engineRevampEnabled
        />
      );
    }

    if (isLibraryReference(apiReference)) {
      return (
        <GlobalOrLibraryContent
          referenceType={ReferenceType.LIBRARY}
          globalOrLibraryReference={apiReference}
          engineRevampEnabled
        />
      );
    }

    if (isGlobalReference(apiReference)) {
      return (
        <GlobalOrLibraryContent
          referenceType={ReferenceType.GLOBAL}
          globalOrLibraryReference={apiReference}
          engineRevampEnabled
        />
      );
    }

    if (isDatatypeReference(apiReference)) {
      return <DatatypeContent datatypeReference={apiReference} engineRevampEnabled />;
    }
    if (isEnumReference(apiReference)) {
      return <EnumContent enumReference={apiReference} engineRevampEnabled />;
    }

    throw Error(`unhandled reference type ${apiReference}`);
  }, [apiReference, classReferenceParents]);

  if (apiReference === null) {
    return (
      <AppLayout
        testid={TestId.ReferenceContainer}
        openGraphMeta={openGraphMeta}
        pageTitle={translate('Label.NullReference') || 'This reference is null!'}
        pageMetaDescription={translate('Label.NullReference') || 'This reference is null!'}
        headings={headings}>
        <AppContent>
          <Typography>{translate('Label.NullReference') || 'This reference is null!'}</Typography>
        </AppContent>
        <AppLeftNav>
          <SideBarNavigation />
        </AppLeftNav>
      </AppLayout>
    );
  }

  const { name, summary, type } = apiReference;
  let tags: EngineTag[] | null = null;
  let deprecationMessage: string | null = null;
  if (isClassReference(apiReference) || isEnumReference(apiReference)) {
    tags = apiReference.tags ?? null;
    deprecationMessage = apiReference.deprecationMessage ?? null;
  }

  const translationKey = headingTranslKeyByReferenceType[type];
  const referenceTypeLabel = translate(translationKey ?? 'Label.API');

  return (
    <AppLayout
      testid={TestId.ReferenceContainer}
      pageTitle={title}
      openGraphMeta={openGraphMeta}
      headings={headings}
      pageMetaDescription={description}
      type={LayoutType.ApiReference}>
      <AppIAHeading />
      <ReferenceHeading
        title={name}
        summary={summary}
        tags={tags}
        referenceType={referenceTypeLabel}
        showSummary={type !== ReferenceType.ENUM}
        currentType={type}
        classReferenceParents={classReferenceParents}
        onTocClick={toggleRightNav}
        deprecationMessage={deprecationMessage}
        hasRightNavItems={hasRightNavItems}
      />
      {/* On desktop, use AppRightNav. On mobile, render drawer directly */}
      {isAppRightNavCompatible && showRightNav && (
        <AppRightNav
          content={
            <EngineReferenceRightNavContent
              headings={headings}
              referenceName={name}
              referenceType={type}
              onClose={() => setShowRightNav(false)}
            />
          }
        />
      )}
      <AppContent>
        <ShowInViewMetadataProvider>{content}</ShowInViewMetadataProvider>
        {/* Render drawer inside AppContent - it uses a portal so it will still appear above everything */}
        {!isAppRightNavCompatible && (
          <EngineReferenceRightNavContent
            headings={headings}
            referenceName={name}
            referenceType={type}
            open={showRightNav}
            onClose={() => setShowRightNav(false)}
          />
        )}
      </AppContent>
      <AppLeftNav>
        <SideBarNavigation />
      </AppLeftNav>
    </AppLayout>
  );
}
